import { c as _c } from "react/compiler-runtime";
import { clearSsoReauthPending, getPendingSsoReauth, githubReconnectLogin, subscribeSsoReauthPending, type PendingSsoReauth } from "@/lib/github-reauth";
import type { SsoBlockedOrg } from "@/hooks/useConnectedGithubOrgs";
import { useEffect, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
const TOAST_ID = "sso-reconnect";

/**
 * Prompt the user to finish reconnecting GitHub after authorizing SAML SSO.
 *
 * Exported separately so Storybook can render the exact production toast.
 */
export function showSsoReconnectToast(onReconnect: () => void = githubReconnectLogin): void {
  // Sonner only routes the close button and swipes through onDismiss, not
  // through cancel.onClick, so both paths must clear the pending flag.
  const dismiss = () => clearSsoReauthPending();
  toast("Authorized on GitHub?", {
    id: TOAST_ID,
    description: "Reconnect to refresh your access.",
    duration: Infinity,
    action: {
      label: "Reconnect",
      onClick: onReconnect
    },
    cancel: {
      label: "Dismiss",
      onClick: dismiss
    },
    onDismiss: dismiss
  });
}
const NO_PENDING_ORGS: string[] = [];
// useSyncExternalStore needs a stable snapshot for unchanged state, so key
// the parsed entry on its stored contents.
let lastPendingKey = "";
let lastPending: PendingSsoReauth | null = null;
const pendingSnapshot = (): PendingSsoReauth | null => {
  const pending = getPendingSsoReauth();
  const key = pending ? `${pending.at}\n${pending.orgs.join("\n")}` : "";
  if (key !== lastPendingKey) {
    lastPendingKey = key;
    lastPending = pending;
  }
  return lastPending;
};
const serverSnapshot = () => null;

/**
 * Watches for the user returning to the dashboard tab after clicking an org's
 * SSO "Authorize" link, and prompts them to complete the second half of the
 * recovery: a silent OAuth re-login that re-authorizes Blacksmith's token for
 * the org(s) they just established SAML sessions for.
 *
 * Returns the blocked orgs whose reconnect is still pending, so the org
 * switcher can swap their Authorize action for Reconnect, and whether the
 * user has come back to a visible tab since flagging one. The flag is set
 * the moment Authorize is clicked, so anything that claims the user has
 * authorized (the toast, the switcher's Reconnect callout) waits for
 * `returnedFromGithub`: a blocked popup or a background tab must not be
 * greeted as a finished SSO round trip.
 *
 * Pass `undefined` while org data is still loading: we can't yet tell whether
 * any orgs are blocked, so the pending flag must be left alone (a full page
 * reload after clicking Authorize would otherwise wipe it before data loads).
 */
export function useSsoReauthPrompt(ssoBlockedOrgs) {
  const $ = _c(10);
  const [returnedForPendingAt, setReturnedForPendingAt] = useState(null);
  const isLoaded = ssoBlockedOrgs !== undefined;
  const hasBlockedOrgs = (ssoBlockedOrgs?.length ?? 0) > 0;
  const pending = useSyncExternalStore(subscribeSsoReauthPending, pendingSnapshot, serverSnapshot);
  const pendingOrgs = pending?.orgs ?? NO_PENDING_ORGS;
  let t0;
  let t1;
  if ($[0] !== pending) {
    t0 = () => {
      if (!pending) {
        toast.dismiss(TOAST_ID);
      }
    };
    t1 = [pending];
    $[0] = pending;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== hasBlockedOrgs || $[4] !== isLoaded) {
    t2 = () => {
      if (!isLoaded) {
        return;
      }
      if (!hasBlockedOrgs) {
        clearSsoReauthPending();
        toast.dismiss(TOAST_ID);
        return;
      }
      const maybePrompt = () => {
        if (document.visibilityState !== "visible") {
          return;
        }
        const current = getPendingSsoReauth();
        if (!current) {
          return;
        }
        setReturnedForPendingAt(current.at);
        showSsoReconnectToast();
      };
      maybePrompt();
      window.addEventListener("focus", maybePrompt);
      document.addEventListener("visibilitychange", maybePrompt);
      return () => {
        window.removeEventListener("focus", maybePrompt);
        document.removeEventListener("visibilitychange", maybePrompt);
      };
    };
    t3 = [isLoaded, hasBlockedOrgs];
    $[3] = hasBlockedOrgs;
    $[4] = isLoaded;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const active = isLoaded && hasBlockedOrgs;
  const t4 = active ? pendingOrgs : NO_PENDING_ORGS;
  const t5 = active && pending !== null && pending.at === returnedForPendingAt;
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      pendingOrgs: t4,
      returnedFromGithub: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}